// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './Delivery.scss';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { onSnapshot, query, setDoc, deleteDoc, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { firestore, storage } from '../context/firebase';
import { format } from 'date-fns';
import { mobileVendor, mobileModel, deviceType } from 'react-device-detect';

import DeliveryOverview from './DeliveryOverview';
import DeliveryScanner from '../components/DeliveryScanner/DeliveryScanner';
import DeliveryImageView from './DeliveryImageView';
import Bale from './Bale';
import LoadingScreen from './LoadingScreen';
import { uploadImage } from '../shared_components/utils/ImgUtils';

// Importing icons
import back_icon from '../assets/icons/white/back_small.png';
import { DELIVERY_ROUTE } from '../constant/routes';
import {
  BALE_DOCUMENT_PATH,
  DELIVERY_DOCUMENT_PATH,
  DELIVERY_IMAGE_DOCUMENT_PATH,
} from '../shared_components/utils/FirebaseUtils';
import { useAuthStore } from '../stores/authStore';

export default function Delivery() {
  const { t } = useTranslation();
  const location = useLocation();
  const deliveryId = location.state.delivery_id;
  const mode = location.state.mode;

  const navigate = useNavigate();

  const currentUserClientId = useAuthStore((state) => state.currentUser?.company?.company);
  const currentUserUid = useAuthStore((state) => state.currentUser?.uid);
  const logEventCustom = useAuthStore((state) => state.logEventCustom);
  const [delivery, setDelivery] = React.useState({});
  const [activeBaleId, setActiveBaleId] = React.useState('');
  const [activeDeliveryImageId, setActiveDeliveryImageId] = React.useState('');
  const [showLoadingScreen, setShowLoadingScreen] = React.useState(false);
  const unsubscribe = React.useRef(null);

  // Subscribe to active delivery and load data
  React.useEffect(() => {
    // Unsubscribe from previous query
    if (unsubscribe.current) unsubscribe.current();

    const deliveryQuery = query(doc(firestore, DELIVERY_DOCUMENT_PATH(currentUserClientId, deliveryId)));
    const newUnsubscribe = onSnapshot(deliveryQuery, (snapshot) => {
      if (snapshot.empty) return;
      setDelivery({
        ...snapshot.data(),
        date: snapshot.data()?.date?.toDate(),
      });
    });
    unsubscribe.current = () => newUnsubscribe();

    // Log event
    logEventCustom('view_delivery', { delivery_id: deliveryId });
  }, [deliveryId]);

  async function addDeliveryImage(image) {
    const deliveryImageId = `${currentUserUid}${new Date().getTime()}`;
    const newDeliveryImage = {
      id: deliveryImageId,
      image_URL: image,
      status: 'uploading',
      processing_status: 'running',
    };
    setDelivery((prevDeliveryInfo) => ({
      ...prevDeliveryInfo,
      bales: { ...prevDeliveryInfo.bales, [deliveryImageId]: newDeliveryImage },
    }));
    setActiveDeliveryImageId(deliveryImageId);

    // Upload the image to storage and retrieve its URL
    const imagePath = `clients/${currentUserClientId}/deliveries/delivery_images/${format(new Date(), 'yyyy-MM-dd')}/${new Date().getTime()}.jpg`;
    const image_URL = await uploadImage(storage, image, imagePath);

    // Create doc for the new delivery image
    const deliveryImageRef = doc(
      firestore,
      DELIVERY_IMAGE_DOCUMENT_PATH(currentUserClientId, deliveryId, deliveryImageId),
    );
    setDoc(deliveryImageRef, {
      id: deliveryImageId,
      client_id: currentUserClientId,
      delivery_id: deliveryId,
      image_URL: image_URL,
      image_storage_path: imagePath,
      status: 'uploaded',
      timestamp: serverTimestamp(),
      processing_status: 'running',
      device_info: {
        vendor: mobileVendor,
        model: mobileModel,
        type: deviceType,
      },
    });

    // Now that the image is uploaded, update the image entry with the URL of the uploaded image.
    newDeliveryImage.status = 'uploaded';
    newDeliveryImage.image_URL = image_URL;
    newDeliveryImage.image_storage_path = imagePath;

    // Add the bale to the delivery
    const deliveryRef = doc(firestore, DELIVERY_DOCUMENT_PATH(currentUserClientId, deliveryId));
    setDoc(
      deliveryRef,
      {
        delivery_images: { [deliveryImageId]: newDeliveryImage },
        last_edit_timestamp: serverTimestamp(),
      },
      { merge: true },
    );

    // Log event
    logEventCustom('add_delivery_image', { delivery_id: deliveryId, delivery_image_id: deliveryImageId });
  }

  function deleteDeliveryImage(deliveryImageId) {
    // Delete the delivery-image -> Firebase functions will take care of the additional cleanup and remove images + bales
    const deliveryImageRef = doc(
      firestore,
      DELIVERY_IMAGE_DOCUMENT_PATH(currentUserClientId, deliveryId, deliveryImageId),
    );
    deleteDoc(deliveryImageRef);
    navigate(-1);

    // Log event
    logEventCustom('delete_delivery_image', { delivery_id: deliveryId, delivery_image_id: deliveryImageId });
  }

  async function deleteDelivery() {
    unsubscribe.current();
    setShowLoadingScreen(true);

    /** Delete delivery from database -> Firebase function will take care of cleanup and delete associated bales & images */
    await deleteDoc(doc(firestore, DELIVERY_DOCUMENT_PATH(currentUserClientId, deliveryId)));

    // Log event
    logEventCustom('delete_delivery', { delivery_id: deliveryId });

    /** Navigate back to deliveries page */
    navigate(-1);
  }

  function handleDeliveryFieldChange(event) {
    /* Change properties of a delivery on change */
    const { name, value } = event.target;
    const deliveryRef = doc(firestore, DELIVERY_DOCUMENT_PATH(currentUserClientId, deliveryId));
    setDoc(deliveryRef, { [name]: value, last_edit_timestamp: serverTimestamp() }, { merge: true });
  }

  function showDeliveryImage(deliveryImageId) {
    setActiveDeliveryImageId(deliveryImageId);
    navigate(DELIVERY_ROUTE, { state: { delivery_id: deliveryId, mode: 'delivery-image-view' } });
  }

  function showBale(baleId, filteredBaleIds = null) {
    setActiveBaleId(baleId);
    navigate(DELIVERY_ROUTE, {
      state: {
        delivery_id: deliveryId,
        mode: 'bale-view',
        filteredBaleIds: filteredBaleIds,
      },
    });
  }

  function importBales(bales) {
    /* 
        Import the bales to the delivery. Should be a dictionary of bales with the baleId as key 
        Bales with selected==True will be added to the delivery, bales with selected==False will be removed from the delivery
        */
    if (!bales) return;

    const batch = writeBatch(firestore);
    for (const baleId of Object.keys(bales)) {
      const baleRef = doc(firestore, BALE_DOCUMENT_PATH(currentUserClientId, deliveryId, baleId));
      batch.update(baleRef, { imported: bales[baleId].selected });
    }
    batch.commit();
  }

  if (showLoadingScreen || !delivery || Object.keys(delivery).length === 0) {
    return <LoadingScreen />;
  } else if (mode === 'scanning') {
    /* Show the bale-scanner page */
    return (
      <div className='delivery'>
        <div className='delivery--header'>
          <div onClick={() => navigate(-1)} className='delivery--back'>
            <img src={back_icon} alt='Back' className='delivery--back-icon' />
          </div>
          <h3 className='delivery--title'>
            {t('delivery')} #{deliveryId}
          </h3>
        </div>

        <DeliveryScanner
          addDeliveryImage={addDeliveryImage}
          viewLastDeliveryImage={() =>
            navigate(DELIVERY_ROUTE, { state: { delivery_id: deliveryId, mode: 'delivery-image-view' } })
          }
          viewOverview={() => navigate(-1)}
          showImageAfterCapture={true}
        />
      </div>
    );
  } else if (mode === 'delivery-image-view') {
    /* Show the delivery-image-view page */
    return (
      <DeliveryImageView
        deliveryId={deliveryId}
        deliveryImageId={activeDeliveryImageId}
        deleteDeliveryImage={deleteDeliveryImage}
        returnToDelivery={() => navigate(-1)}
        importBales={importBales}
        deliveryImages={delivery.delivery_images}
        setActiveDeliveryImageId={setActiveDeliveryImageId}
      />
    );
  } else if (mode === 'bale-view') {
    /* Show the bale-view page */
    const filteredBaleIds = location.state?.filteredBaleIds || null;
    return (
      <>
        <Bale
          activeBaleId={activeBaleId}
          deliveryId={deliveryId}
          returnToDelivery={() => {
            navigate(-1);
          }}
          bales={delivery.bales}
          setActiveBaleId={setActiveBaleId}
          filteredBaleIds={filteredBaleIds}
        />
      </>
    );
  } else {
    /* Show the delivery-overview page */
    return (
      <DeliveryOverview
        handleDeliveryFieldChange={handleDeliveryFieldChange}
        deleteDelivery={deleteDelivery}
        deliveryId={deliveryId}
        delivery={delivery}
        showDeliveryImage={showDeliveryImage}
        showBale={showBale}
        deliveriesPageData={location.state.deliveriesPageData}
      />
    );
  }
}
